import React, { useState, useEffect, useCallback } from "react";
import { LiveKitRoom, RoomAudioRenderer } from "@livekit/components-react";
import { Heart, Eye, Circle, Share2, Loader2, WifiOff } from "lucide-react";
import { toast } from "sonner";
import { usePoll } from "@/hooks/usePoll";
import { LiveStage } from "@/components/live/LiveStage";
import { LiveChat } from "@/components/live/LiveChat";
import { api, shareLink } from "@/lib/api";
import { cn } from "@/lib/utils";

export function LiveBadge({ className }) {
  return <span data-testid="live-badge" className={cn("inline-flex items-center gap-1.5 rounded-full bg-red-500 px-2.5 py-1 text-[11px] font-black uppercase tracking-wide text-white", className)}><span className="h-1.5 w-1.5 animate-pulse rounded-full bg-white" /> Live</span>;
}

export function HeartBurst({ count }) {
  return <span data-testid="live-hearts" className="inline-flex items-center gap-1 text-sm font-semibold text-white"><Heart className="h-4 w-4 fill-aura-magenta text-aura-magenta" /> {count}</span>;
}

// Viewer experience: WebRTC playback of the founders stage, live chrome, hearts and chat.
export function LivePlayer({ broadcastId, initial, onEnded }) {
  const [b, setB] = useState(initial || null);
  const [conn, setConn] = useState(null); // {token,url}
  const [err, setErr] = useState(null);
  const [hearts, setHearts] = useState(initial?.hearts || 0);
  const [floating, setFloating] = useState([]);

  useEffect(() => {
    let dead = false;
    api.liveJoin(broadcastId).then((r) => { if (!dead) { setConn({ token: r.token, url: r.url }); setB(r.broadcast); } })
      .catch((e) => { if (!dead) setErr(e.message); });
    return () => { dead = true; };
  }, [broadcastId]);

  const refresh = useCallback(async () => {
    try { const nb = await api.liveBroadcast(broadcastId); setB(nb); setHearts(nb.hearts); if (nb.status === "ended") onEnded && onEnded(nb); } catch {}
  }, [broadcastId, onEnded]);
  usePoll(refresh, 5000, [broadcastId]);
  usePoll(async () => { if (b?.status === "live") { try { await api.livePresence(broadcastId); } catch {} } }, 15000, [broadcastId, b?.status]);

  const heart = async () => {
    const id = Math.random().toString(36).slice(2);
    setFloating((f) => [...f, { id, x: 10 + Math.random() * 60 }]);
    setTimeout(() => setFloating((f) => f.filter((x) => x.id !== id)), 1500);
    setHearts((h) => h + 1);
    try { const r = await api.liveHeart(broadcastId); setHearts(r.hearts); } catch {}
  };
  const share = async () => {
    const r = await shareLink(`${window.location.origin}/live/founders`, b?.title || "AuraMax Founders LIVE");
    if (r === "copied") toast("Link copied");
  };

  if (!b) return <div className="grid aspect-video w-full place-items-center rounded-2xl bg-am-surface2"><Loader2 className="h-6 w-6 animate-spin text-am-text3" /></div>;
  const ended = b.status !== "live";

  return (
    <div data-testid="live-player" className="grid gap-3 lg:grid-cols-[1fr_340px]">
      <div className="relative aspect-[9/16] max-h-[78vh] w-full overflow-hidden rounded-2xl bg-black sm:aspect-video">
        {ended ? (
          <div data-testid="live-ended" className="flex h-full w-full flex-col items-center justify-center gap-2 text-am-text2"><Circle className="h-6 w-6" /><span className="text-sm font-semibold text-white">This broadcast has ended</span><span className="text-xs">{b.replay_available ? "Replay is ready below." : "Replay unavailable."}</span></div>
        ) : err ? (
          <div data-testid="live-unavailable" className="flex h-full w-full flex-col items-center justify-center gap-2 px-6 text-center text-am-text2"><WifiOff className="h-6 w-6" /><span className="text-sm font-semibold text-white">Stream unavailable</span><span className="text-xs">{err}</span></div>
        ) : conn ? (
          <LiveKitRoom serverUrl={conn.url} token={conn.token} connect video={false} audio={false} options={{ adaptiveStream: true, dynacast: true }} onError={(e) => setErr(e.message)} className="h-full w-full">
            <LiveStage layout={b.layout} spotlight={b.spotlight} hostIds={b.hosts.map((h) => h.user_id)} />
            <RoomAudioRenderer />
          </LiveKitRoom>
        ) : <div className="grid h-full w-full place-items-center"><Loader2 className="h-6 w-6 animate-spin text-am-text3" /></div>}

        <div className="pointer-events-none absolute inset-x-0 top-0 flex items-center gap-2 p-3">
          {!ended && <LiveBadge />}
          <span data-testid="live-viewers" className="inline-flex items-center gap-1 rounded-full bg-black/55 px-2.5 py-1 text-xs font-semibold text-white backdrop-blur"><Eye className="h-3.5 w-3.5" /> {b.viewers}</span>
          {b.recording_active && <span data-testid="live-rec" className="ml-auto inline-flex items-center gap-1 rounded-full bg-black/55 px-2.5 py-1 text-[11px] font-black text-red-400 backdrop-blur"><span className="h-2 w-2 animate-pulse rounded-full bg-red-500" /> REC</span>}
        </div>
        {floating.map((f) => <Heart key={f.id} className="pointer-events-none absolute bottom-16 h-7 w-7 fill-aura-magenta text-aura-magenta animate-[fade-up_1.4s_ease-out_forwards]" style={{ left: `${f.x}%` }} />)}
        <div className="absolute inset-x-0 bottom-0 flex items-center gap-3 p-3">
          <span className="truncate text-sm font-bold text-white drop-shadow">{b.title}</span>
          <span className="ml-auto flex items-center gap-2">
            {!ended && <button onClick={heart} data-testid="live-heart" className="grid h-10 w-10 place-items-center rounded-full bg-black/55 text-white backdrop-blur active:scale-90"><Heart className="h-5 w-5" /></button>}
            <button onClick={share} data-testid="live-share" className="grid h-10 w-10 place-items-center rounded-full bg-black/55 text-white backdrop-blur"><Share2 className="h-4 w-4" /></button>
          </span>
        </div>
      </div>
      <div className="flex flex-col gap-2">
        <div className="flex items-center justify-between px-1 text-xs text-am-text2"><HeartBurst count={hearts} /><span>{b.hosts.map((h) => `@${h.username}`).join(" · ")}</span></div>
        <LiveChat broadcast={b} canModerate={b.is_staff || b.is_host} onBroadcast={setB} className="h-[360px] lg:h-[calc(78vh-32px)]" />
      </div>
    </div>
  );
}
